import React, { useContext, useState, useEffect } from "react";
import { Form, Input, Button } from "antd";
import { CheckOutlined, CloseOutlined, UserOutlined } from "@ant-design/icons";
import { TResponseModel } from "../../types/config";
import { useNavigate } from "react-router-dom";
import useFetch from "../../hooks/useFetch";
import { eFONTFAMILY, eResultCode } from "../../utils/enum";
import useToast from "../../hooks/useToast";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import styles from "./auth.module.css";
import { UserContext } from "../../context";
import EncryptUtils from "../../utils/encrypt";
import {
  ChangePassword,
  GenerateOtp,
  ValidateOTP,
} from "../../utils/api.constant";

type TProps = {
  handleModalCancel?: () => void;
};

const SendOTP: React.FC<TProps> = ({ handleModalCancel }) => {
  const context: any = useContext(UserContext);
  const navigate = useNavigate();
  const { post } = useFetch();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOTPSent, setIsOTPSent] = useState<boolean>(false);
  const [isResendMode, setIsResendMode] = useState<boolean>(false);
  const [timer, setTimer] = useState<number>(0);
  const [targetTime, setTargetTime] = useState<number>(0);
  const [otp, setOtp] = useState<string>("");
  const [isOTPVerified, setIsOTPVerified] = useState<boolean>(false);
  const [otpToken, setOTPToken] = useState<string>("");
  const [form] = Form.useForm();
  const emailId = context.user?.emailId;

  useEffect(() => {
    if (emailId) {
      form.setFieldsValue({ email: emailId });
    }
  }, [emailId, form]);

  const onSendOTP = async (values: { emailId: string }) => {
    try {
      setIsLoading(true);
      const payload = {
        data: {
          emailId: emailId,
        },
      };
      const response: TResponseModel = await post(GenerateOtp, payload);

      const { dataResponse, data } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        setOTPToken(data?.otpToken);
        setIsOTPSent(true);
        setIsResendMode(false);
        setTimer(300); // Start 5-minute timer
        setTargetTime(Date.now() + 300 * 1000);
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onVerifyOTP = async (values: { otp: string }) => {
    try {
      setIsLoading(true);
      const payload = {
        data: {
          emailId: emailId,
          otp: values.otp,
          otpToken,
        },
      };
      const response: TResponseModel = await post(ValidateOTP, payload);

      const { dataResponse, data, authToken } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        const decryptedData = EncryptUtils.decrypt(data?.otpToken);
        if (values.otp === decryptedData.split(":")[1]) {
          onShowToast({
            type: ToastType.success,
            title: <CheckOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
          setIsOTPVerified(true);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CheckOutlined />,
            position: ToastOpen.centerTop,
            content: "Error validating OTP, please try again!",
          });
        }
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitNewPassword = async (values: {
    email: string;
    password: string;
    confirmPassword: string;
  }) => {
    if (values.password !== values.confirmPassword) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Passwords do not match!",
      });
      return;
    }

    try {
      setIsLoading(true);
      const email = context.user?.emailId;
      const password = EncryptUtils.encrypt(values.password as string);
      const encryptedOTP = EncryptUtils.encrypt(otp as string);
      const payload = {
        data: {
          emailId: EncryptUtils.encrypt(email),
          password: password,
          otp: encryptedOTP,
        },
      };
      const response: TResponseModel = await post(ChangePassword, payload);

      const { dataResponse } = response;
      const { returnCode, description } = dataResponse;

      if (returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
        // navigate("/login"); // Redirect to login after password reset
        handleModalCancel ? handleModalCancel?.() : navigate("/login");
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: description,
        });
      }
    } catch (error) {
      console.error("Error submitting new password:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Timer countdown
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (targetTime > 0) {
      interval = setInterval(() => {
        const secondsRemaining = Math.ceil((targetTime - Date.now()) / 1000);

        if (secondsRemaining <= 0) {
          setTimer(0);
          setIsResendMode(true);
          setOtp("");
          form.resetFields(["otp"]);
          setTargetTime(0);
          clearInterval(interval);
        } else {
          setTimer(secondsRemaining);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [targetTime, form]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div style={{ margin: "3rem" }}>
      {!isOTPVerified ? (
        !isOTPSent ? (
          <Form
            form={form}
            name="forgotPassword"
            onFinish={onSendOTP}
            size="middle"
            autoComplete="off"
            initialValues={{ email: emailId }}
          >
            <p>Please enter your email to receive an OTP</p>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Please input your Email!" },
                { type: "email", message: "Please enter a valid Email!" },
              ]}
            >
              <Input
                autoComplete="off"
                prefix={<UserOutlined />}
                placeholder="Email"
                disabled
              />
            </Form.Item>
            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Send OTP
              </Button>
            </Form.Item>
          </Form>
        ) : (
          <Form name="verifyOTP" onFinish={onVerifyOTP} size="middle">
            <p>Enter the OTP sent to your email address</p>
            <Form.Item
              name="otp"
              rules={[{ required: true, message: "Please input the OTP!" }]}
            >
              <Input
                autoComplete="off"
                prefix={<UserOutlined />}
                placeholder="OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              {timer === 0 && (
                <p style={{ color: "red", fontSize: "12px" }}>
                  *OTP has expired. Please resend a new OTP.
                </p>
              )}
            </Form.Item>
            <Form.Item>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <p className={styles.timer} style={{ marginTop: "5px" }}>
                  Remaining time :{" "}
                  <span style={{ fontWeight: "600" }}>{formatTime(timer)}</span>
                </p>
                <Button
                  type="link"
                  disabled={!isResendMode}
                  onClick={() => onSendOTP({ emailId: emailId })}
                >
                  Resend OTP?
                </Button>
              </div>
            </Form.Item>
            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Verify OTP
              </Button>
            </Form.Item>
          </Form>
        )
      ) : (
        <Form
          name="newPassword"
          onFinish={onSubmitNewPassword}
          size="middle"
          autoComplete="off"
        >
          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Please input your new password!" },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message:
                  "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
              },
            ]}
          >
            <Input.Password autoComplete="off" placeholder="New Password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            rules={[
              { required: true, message: "Please confirm your new password!" },
              {
                pattern:
                  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                message:
                  "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character!",
              },
            ]}
          >
            <Input.Password
              autoComplete="off"
              placeholder="Confirm New Password"
            />
          </Form.Item>
          <Form.Item>
            <Button block type="primary" htmlType="submit" loading={isLoading}>
              Submit
            </Button>
          </Form.Item>
        </Form>
      )}
    </div>
  );
};

export default SendOTP;
